// src/services/protocolVersions.js
//
// Protocol versions on the page side (2026-10-22): which version a protocol
// is, what the next one would be, and whether SourceX's built source documents
// were made before the current version. PURE (no Firebase, no React).
// Version docs are written only by the addProtocolVersion function; the rules
// for numbering live in functions/protocolVersionPlan.js.
//
// Old protocols are left alone: until a study's first new version, nothing has
// a number. Version 1 is then the protocol the study was using.

const toMs = (t) => {
  if (!t) return 0;
  if (typeof t.toMillis === "function") return t.toMillis();
  if (t instanceof Date) return t.getTime();
  return Number(t) || 0;
};

const byVersion = (versions) => [...(versions || [])].filter((v) => Number.isInteger(v?.version)).sort((a, b) => a.version - b.version);

/** The current (newest) version doc, or null when the study has none yet. */
export const currentVersion = (versions) => {
  const vs = byVersion(versions);
  return vs.length ? vs[vs.length - 1] : null;
};

/**
 * For the "Is this a new version?" question, asked before an upload when the
 * study already has a protocol. With no versions recorded yet, the protocol in
 * use counts as Version 1, so a new one would be Version 2.
 * @returns {{ask: boolean, current: number|null, next: number}}
 */
export const newVersionQuestion = (versions, protocols) => {
  const cur = currentVersion(versions);
  if (cur) return { ask: true, current: cur.version, next: cur.version + 1 };
  const hasProtocol = (protocols || []).length > 0;
  return hasProtocol ? { ask: true, current: 1, next: 2 } : { ask: false, current: null, next: 1 };
};

/**
 * A protocol's label: "Version 3", "Version 2 · re-upload", or "Waiting for an
 * admin" (sent as a new version). A re-upload is any protocol that isn't a
 * version itself: it belongs to the version that was current when it was
 * uploaded. Protocols from before the first version get no label.
 */
export const versionLabel = (protocol, versions, requests = []) => {
  if (!protocol) return null;
  const vs = byVersion(versions);
  const own = vs.find((v) => v.protocolId === protocol.id);
  if (own) return `Version ${own.version}`;
  if ((requests || []).some((r) => r.protocolId === protocol.id && r.status === "pending")) return "Waiting for an admin";
  const at = toMs(protocol.uploadedAt || protocol.createdAt);
  const under = vs.filter((v) => toMs(v.addedAt) && toMs(v.addedAt) <= at).pop();
  return under ? `Version ${under.version} · re-upload` : null;
};

/**
 * SourceX's built source documents: was a newer protocol version (2 or later)
 * added after these documents were built? Then each visit asks to be checked
 * against it (per-visit checks, protocolVersionService.checkVisit).
 * @param {{versions: object[], builtAt: any}} p  builtAt: when the documents were generated
 * @returns {null | {version: number, fileName: string, addedAt: number}}
 */
export const sourceDocsNotice = ({ versions, builtAt }) => {
  const cur = currentVersion(versions);
  if (!cur || cur.version < 2) return null;
  const built = toMs(builtAt);
  if (!built || built >= toMs(cur.addedAt)) return null;
  return { version: cur.version, fileName: cur.fileName || "", addedAt: toMs(cur.addedAt) };
};

/**
 * Which version a protocol belongs to: its own version, else the version that
 * was current when it was uploaded (a re-upload). 0 = uploaded before the first
 * version was recorded (older than every version). null = the study has no versions.
 */
export const effectiveVersion = (protocol, versions) => {
  const vs = byVersion(versions);
  if (!vs.length || !protocol) return null;
  const own = vs.find((v) => v.protocolId === protocol.id);
  if (own) return own.version;
  const at = toMs(protocol.uploadedAt || protocol.createdAt);
  const under = vs.filter((v) => toMs(v.addedAt) && toMs(v.addedAt) <= at).pop();
  return under ? under.version : 0;
};

/**
 * SourceX: what a protocol may be used for once a study has versions
 * (decided 2026-10-23). The current version (and its re-uploads) comes first.
 * An OLDER version is locked (no Review, no Approve, no Read) unless source
 * documents were already built, in which case nothing is locked and nothing
 * is pushed: the built documents are edited, not rebuilt.
 * @param {object} protocol
 * @param {object[]} versions
 * @param {{docsBuilt: boolean}} opts
 * @returns {{version: number|null, current: boolean, older: boolean, locked: boolean, prioritize: boolean}}
 */
export const protocolRole = (protocol, versions, { docsBuilt = false } = {}) => {
  const cur = currentVersion(versions);
  const v = effectiveVersion(protocol, versions);
  if (!cur || v == null) return { version: null, current: false, older: false, locked: false, prioritize: false };
  const current = v === cur.version;
  const older = cur.version >= 2 && v < cur.version;
  return { version: v, current, older, locked: older && !docsBuilt, prioritize: current && cur.version >= 2 && !docsBuilt };
};

/**
 * Everything a SourceX step page needs about versions, from what it already
 * loads: each protocol's role, whether it's locked, and `readFirst` when the
 * current version (2+) hasn't been read at all yet and no documents are built.
 */
export const sourcexVersionGuidance = (protocols, versions, { docsBuilt = false } = {}) => {
  const cur = currentVersion(versions);
  const roleOf = (p) => protocolRole(p, versions, { docsBuilt });
  const isLocked = (p) => roleOf(p).locked;
  let readFirst = null;
  if (cur && cur.version >= 2 && !docsBuilt) {
    const mine = (protocols || []).filter((p) => roleOf(p).current);
    const started = mine.some((p) => p.status && p.status !== "uploaded" && p.status !== "failed");
    if (!started && mine.some((p) => p.status === "uploaded")) readFirst = { version: cur.version };
  }
  return { current: cur, roleOf, isLocked, readFirst };
};

const WHERE = { "study-page": "on the study page", "screening-card": "in Screening Visit", sourcex: "in SourceX" };
const tsOf = (p) => toMs(p?.uploadedAt || p?.createdAt);

/**
 * The current protocol, for "already uploaded" prompts: its version, file, who
 * uploaded it and where. With no versions yet, the protocol in use (newest
 * approved, else newest) counts as Version 1. null when the study has none.
 * @returns {null | {version:number, fileName:string, by:string, where:string, when:number}}
 */
export const currentProtocolInfo = (versions, protocols) => {
  const list = protocols || [];
  const cur = currentVersion(versions);
  if (cur) {
    const p = list.find((x) => x.id === cur.protocolId) || {};
    const by = cur.requestedBy || (cur.source === "existing" ? p.createdBy : cur.addedBy) || p.createdBy || "";
    return { version: cur.version, fileName: cur.fileName || p.fileName || "", by, where: WHERE[cur.source] || "", when: toMs(cur.addedAt) || tsOf(p) };
  }
  if (!list.length) return null;
  const newestFirst = (a, b) => tsOf(b) - tsOf(a);
  const inUse = list.filter((p) => p.status === "approved").sort(newestFirst)[0] || [...list].sort(newestFirst)[0];
  return { version: 1, fileName: inUse.fileName || "", by: inUse.createdBy || "", where: "", when: tsOf(inUse) };
};

/** "Version 1 · file.pdf, uploaded by x@y in SourceX on Oct 22, 2026" */
export const describeCurrentProtocol = (info) => {
  if (!info) return "";
  const when = info.when ? new Date(info.when).toLocaleDateString(undefined, { dateStyle: "medium" }) : "";
  return `Version ${info.version}${info.fileName ? ` · ${info.fileName}` : ""}${info.by ? `, uploaded by ${info.by}` : ""}${info.where ? ` ${info.where}` : ""}${when ? ` on ${when}` : ""}`;
};

/**
 * The number a waiting request would get if approved now. A study's first
 * protocol (nothing else uploaded) is Version 1.
 */
export const nextVersionFor = (versions, protocols, requestProtocolId) => {
  const cur = currentVersion(versions);
  if (cur) return cur.version + 1;
  return (protocols || []).some((p) => p.id !== requestProtocolId) ? 2 : 1;
};
